import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Heart, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import VerifiedHostBadge from './VerifiedHostBadge';
import SustainabilityBadge from './SustainabilityBadge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get the correct image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
  // If it's already a full URL, return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // If it's a storage path or upload path, prepend backend URL
  if (imagePath.startsWith('/api/')) {
    return `${BACKEND_URL}${imagePath}`;
  }
  if (imagePath.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${imagePath}`;
  }
  // Default fallback
  return imagePath;
};

const PropertyCard = ({ property }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const autoSlideInterval = useRef(null);

  // Check wishlist status on mount
  useEffect(() => {
    if (isAuthenticated && user?.user_id) {
      const propertyId = property._id || property.id;
      axios.get(`${BACKEND_URL}/api/wishlist/check/${user.user_id}/${propertyId}`)
        .then(res => { if (res.data.is_wishlisted) setIsFavorite(true); })
        .catch(() => {});
    }
  }, [isAuthenticated, user, property]);

  // Auto-slide images on hover
  useEffect(() => {
    if (isHovering && property.images && property.images.length > 1) {
      autoSlideInterval.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
      }, 1500); // Change image every 1.5 seconds
    } else {
      if (autoSlideInterval.current) {
        clearInterval(autoSlideInterval.current);
        autoSlideInterval.current = null;
      }
    }

    return () => {
      if (autoSlideInterval.current) {
        clearInterval(autoSlideInterval.current);
      }
    };
  }, [isHovering, property.images]);

  const handleCardClick = () => {
    const route = property.slug || property._id || property.id;
    navigate(`/property/${route}`);
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      setShowLoginDialog(true);
      return;
    }
    
    const propertyId = property._id || property.id;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/wishlist/toggle`, {
        user_id: user.user_id,
        property_id: propertyId
      });
      if (res.data.action === 'added') {
        setIsFavorite(true);
        toast.success('Added to wishlist');
      } else {
        setIsFavorite(false);
        toast.info('Removed from wishlist');
      }
    } catch (err) {
      toast.error('Failed to update wishlist');
    }
  };

  const handleLoginRedirect = () => {
    setShowLoginDialog(false);
    navigate('/login');
  };

  const handleImageChange = (direction, e) => {
    e.stopPropagation();
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setCurrentImageIndex(0); // Reset to first image when mouse leaves
  };

  return (
    <Card
      className="group cursor-pointer border-0 shadow-none overflow-hidden"
      onClick={handleCardClick}
    >
      {/* Image Container */}
      <div 
        className="relative aspect-square overflow-hidden rounded-xl mb-3"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <img
          src={getImageUrl(property.images[currentImageIndex])}
          alt={property.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
          }}
        />
        
        {/* Favorite Button */}
        <button
          onClick={handleFavoriteClick}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/80 hover:bg-white hover:scale-110 transition-all"
        >
          <Heart
            className={`h-5 w-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-700'}`}
          />
        </button>

        {/* Image Navigation Dots */}
        {property.images.length > 1 && (
          <>
            <button
              onClick={(e) => handleImageChange('prev', e)}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:scale-110"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => handleImageChange('next', e)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:scale-110"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-1">
              {property.images.map((img, index) => (
                <div
                  key={`${img}-${index}`}
                  className={`h-1.5 rounded-full transition-all ${
                    index === currentImageIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/60'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Instant Book Badge */}
        {property.instantBook && (
          <Badge className="absolute bottom-3 left-3 bg-white text-gray-900 hover:bg-white">
            Instant Book
          </Badge>
        )}

        {/* KYC Pending Badge — host has not completed PAN verification yet */}
        {property.host_verified === false && (
          <Badge
            className="absolute top-3 left-3 bg-amber-500 hover:bg-amber-500 text-white shadow-sm"
            data-testid="kyc-pending-badge"
            title="Host is verifying identity. Booking is disabled until verification completes."
          >
            KYC Pending
          </Badge>
        )}
      </div>

      {/* Property Info */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 truncate">
            {property.location}
          </h3>
          <div className="flex items-center space-x-1">
            <Star className="h-4 w-4 fill-current text-gray-900" />
            <span className="text-sm font-medium">{property.rating}</span>
          </div>
        </div>
        
        <p className="text-sm text-gray-500 truncate">{property.title}</p>
        
        <div className="text-sm text-gray-500">
          {property.guests} guests · {property.bedrooms} bedrooms · {property.beds} beds
        </div>
        
        <div className="pt-1 flex items-center justify-between gap-2">
          <div>
            <span className="font-semibold text-gray-900">₹{property.price.toLocaleString()}</span>
            <span className="text-gray-500"> / night</span>
          </div>
          <div className="flex items-center gap-1 flex-wrap justify-end">
            {property.sustainability?.verified && property.sustainability?.verified_tier && (
              <SustainabilityBadge tier={property.sustainability.verified_tier} score={property.sustainability.score} size="sm" />
            )}
            {property.host_verified && <VerifiedHostBadge size="sm" />}
          </div>
        </div>
      </div>

      {/* Login Required Dialog */}
      <AlertDialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Login Required</AlertDialogTitle>
            <AlertDialogDescription>
              Please login or create an account to add properties to your wishlist.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.stopPropagation();
                handleLoginRedirect();
              }}
              className="bg-[#7CB342] hover:bg-[#6da036]"
            >
              Login
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default PropertyCard;